import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Components
import Loader from 'components/Loader/Loader';

// Utils
import User from 'utils/user.util';

const handleOAuth2 = async (app, name, connectionId = null) => {
    const rawState = {
        name,
        app_id: app.id,
        connection_id: connectionId, // Pass if reauth
        redirect: `${window.location.origin}/settings/connections/oauth2`,
    };

    return fetch(
        process.env.REACT_APP_BACKEND_URL + '/api/connected_apps/services/oauth2/get_url',
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${User.getToken()}`,
            },
            body: JSON.stringify({
                app_id: app.id,
                connection_id: connectionId,
                state: encodeURIComponent(JSON.stringify(rawState)),
            }),
        }
    )
        .then((response) => response.json())
        .then((response) => {
            if (response?.error) {
                return { error: true };
            }
            window.location.href = response?.oauth_url;
        })
        .catch((e) => console.log(e));
};

const OAuth2 = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const code = searchParams.get('code');
    let app_id, name, connection_id;

    const saveCredentials = (credentials) => {
        if (!credentials) throw Error('Missing Credentials');

        // Split credentials and meta data
        const meta_data = { ...credentials?.meta_data };
        delete credentials?.meta_data;

        const method = connection_id ? 'PATCH' : 'POST';
        fetch(process.env.REACT_APP_BACKEND_URL + '/api/connected_apps/tenant/connections', {
            method,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${User.getToken()}`,
            },
            body: JSON.stringify({
                name,
                app_id,
                connection_id, // Used for PATCH
                app_credentials: credentials,
                meta_data: meta_data,
            }),
        })
            .then(async (res) => {
                if (!res.ok) {
                    let msg = 'Failed to save credentials';
                    const data = await res.json();
                    msg = data?.error || data?.message || msg;
                    navigate(`/settings/connections?error=${encodeURIComponent(msg)}`);
                    return;
                }
                navigate('/settings/connections');
            })
            .catch((err) =>
                navigate(
                    `/settings/connections?error=${encodeURIComponent(
                        err?.message || 'Network error'
                    )}`
                )
            );
    };

    /**
     * Exchange short-lived token for long-term token.
     */
    const tokenExchange = async () => {
        fetch(
            process.env.REACT_APP_BACKEND_URL +
                '/api/connected_apps/services/oauth2/exchange_token',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${User.getToken()}`,
                },
                body: JSON.stringify({
                    app_id,
                    code,
                }),
            }
        )
            .then((res) => res.json())
            .then((res) => {
                if (!res.error) {
                    saveCredentials(res);
                } else {
                    navigate(`/settings/connections?error=${encodeURIComponent(res.error)}`);
                }
            })
            .catch((error) => {
                navigate(`/settings/connections?error=${encodeURIComponent(error)}`);
            });
    };

    useEffect(() => {
        try {
            const raw = searchParams.get('state') || '';
            const parsed = JSON.parse(decodeURIComponent(raw));
            ({ app_id, name, connection_id } = parsed || {});
            if (!app_id || !name) {
                navigate(
                    `/settings/connections?error=${encodeURIComponent(
                        'Missing required OAuth state fields'
                    )}`
                );
                return;
            }
        } catch (e) {
            navigate(`/settings/connections?error=${encodeURIComponent('Invalid OAuth state')}`);
            return null;
        }
        tokenExchange();
    }, []);

    return <Loader />;
};

export { OAuth2, handleOAuth2 };
